import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_name: "", customer_id: "", project_type: "Residential" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/projects/statuses").then((r) => setStatuses(r.data.statuses)).catch(() => {});
    if (can("customers.view")) api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const filtered = useMemo(
    () => projects.filter((p) =>
      (!q || p.project_name.toLowerCase().includes(q.toLowerCase()) || p.project_code.toLowerCase().includes(q.toLowerCase())) &&
      (!filter || p.status === filter)
    ),
    [projects, q, filter]
  );

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, customer_id: form.customer_id || null };
      const { data } = await api.post("/projects", payload);
      toast.success(`Created ${data.project_code}`);
      setOpen(false); setForm({ project_name: "", customer_id: "", project_type: "Residential" });
      load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <AppShell title="Projects">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="projects-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…"
            className="pl-9 pr-3 py-2 text-sm rounded-md border border-[#D7C6A9] bg-white w-64 outline-none focus:ring-2 focus:ring-[#F56B1F]"
          />
        </div>
        <select data-testid="projects-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="text-sm rounded-md border border-[#D7C6A9] bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-[#F56B1F]">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex-1" />
        {can("projects.create") && (
          <button onClick={() => setOpen(true)} data-testid="projects-new-button" className="flex items-center gap-2 rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white text-sm font-medium px-4 py-2 transition-colors shadow-sm">
            <Plus size={16} /> New Project
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]">
              <th className="px-5 py-3 font-medium">Project ID</th>
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Customer</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">Designer</th>
              <th className="px-5 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid={`projects-table-row-${p.project_code}`} className="border-t border-[#EEE9DE] hover:bg-[#EEE9DE] cursor-pointer transition-colors">
                <td className="px-5 py-3.5 mono text-neutral-700">{p.project_code}</td>
                <td className="px-5 py-3.5 text-neutral-900 font-medium">{p.project_name}</td>
                <td className="px-5 py-3.5 text-neutral-600">{p.customer ? `${p.customer.first_name} ${p.customer.last_name}` : "—"}</td>
                <td className="px-5 py-3.5 text-neutral-600">{p.project_type}</td>
                <td className="px-5 py-3.5 text-neutral-600">{p.designer?.name || "—"}</td>
                <td className="px-5 py-3.5"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-neutral-400">No projects match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Project name</label>
              <input data-testid="new-project-name-input" required value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]" placeholder="e.g. Meena Villa — Coimbatore" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Customer (optional)</label>
              <select data-testid="new-project-customer-select" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]">
                <option value="">— None —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.customer_code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Type</label>
              <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F56B1F]">
                {["Residential", "Commercial", "Hospitality", "Bespoke"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <button data-testid="new-project-submit" disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-md bg-[#F56B1F] hover:bg-[#D65A10] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Create Project
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
